/**
 * Compatibility shim.
 *
 * This project previously ran TWO toast systems side by side: the legacy
 * shadcn/radix toaster (this hook + `@/components/ui/toaster`) and sonner.
 * Both were mounted in `App.tsx`, so the same event could render two toasts
 * in two different visual styles, stacked in two different corners.
 *
 * There is now exactly one renderer: sonner (`@/components/ui/sonner`).
 * This hook keeps the old `toast({ title, description, variant })` call
 * signature working — 60+ call sites — and forwards to sonner, with
 * deduplication so an identical message fired twice updates one toast
 * instead of stacking.
 */
import type * as React from "react";
import { toast as sonnerToast } from "sonner";

type ToastVariant = "default" | "destructive" | "success" | "warning" | "info";

export interface ToastOptions {
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: ToastVariant;
  duration?: number;
  action?: React.ReactNode;
  id?: string;
  [key: string]: unknown;
}

const stringify = (value: React.ReactNode): string =>
  typeof value === "string" || typeof value === "number" ? String(value) : "";

/** Stable id derived from the content so repeats collapse into one toast. */
const dedupeId = (options: ToastOptions): string => {
  if (options.id) return options.id;
  const raw = `${options.variant ?? "default"}|${stringify(options.title)}|${stringify(options.description)}`;
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    hash = (hash << 5) - hash + raw.charCodeAt(i);
    hash |= 0;
  }
  return `t${hash}`;
};

function toast(options: ToastOptions) {
  const { title, description, variant = "default", duration, action } = options;
  const id = dedupeId(options);

  const message = title ?? description ?? "";
  const payload = {
    id,
    duration,
    description: title ? description : undefined,
    action: action as never,
  };

  switch (variant) {
    case "destructive":
      sonnerToast.error(message as string, payload);
      break;
    case "success":
      sonnerToast.success(message as string, payload);
      break;
    case "warning":
      sonnerToast.warning(message as string, payload);
      break;
    case "info":
      sonnerToast.info(message as string, payload);
      break;
    default:
      sonnerToast(message as string, payload);
  }

  return {
    id,
    dismiss: () => sonnerToast.dismiss(id),
    update: (next: ToastOptions) => toast({ ...next, id }),
  };
}

function useToast() {
  return {
    /** Legacy field: the radix renderer is gone, nothing reads this. */
    toasts: [] as never[],
    toast,
    dismiss: (toastId?: string) => sonnerToast.dismiss(toastId),
  };
}

export { useToast, toast };
